import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@workspace/ui/components/sheet'
import { cn } from '@workspace/ui/lib/utils'
import type { LucideIcon } from 'lucide-react'
import { cloneElement, type ReactElement, useRef, useState } from 'react'
import { useMediaQuery } from '../../../hooks/use-media-query'

export type MenuAction = {
  key: string
  title: string
  icon?: LucideIcon
  /** A line under the title, as the page menus have. */
  hint?: string
  destructive?: boolean
  onSelect: () => void
}

function ActionText({ action }: { action: MenuAction }) {
  const Icon = action.icon
  return (
    <>
      {Icon && (
        <Icon className={cn('size-4 shrink-0 text-muted-foreground', action.hint && 'mt-0.5')} />
      )}
      <span className="block min-w-0">
        <span className="block text-sm text-foreground">{action.title}</span>
        {action.hint && (
          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
            {action.hint}
          </span>
        )}
      </span>
    </>
  )
}

/**
 * A menu of actions: a dropdown under its button, and on a phone (below 640px) a sheet from the
 * bottom edge, whose rows are large enough to press. `trigger` is the one button that opens it.
 */
export function ActionMenu({
  trigger,
  label,
  actions,
  className,
}: {
  trigger: ReactElement<{ onClick?: () => void }>
  /** What the sheet is called for screen readers. */
  label: string
  actions: MenuAction[]
  /** The dropdown's width, as classes. */
  className?: string
}) {
  const phone = useMediaQuery('(max-width: 639px)')
  const [open, setOpen] = useState(false)
  // An action may open a window of its own. It runs once the menu has closed and let go of focus:
  // opened while the menu still holds it, the window is dismissed at once.
  const chosen = useRef<MenuAction | null>(null)
  if (actions.length === 0) return null

  if (phone) {
    return (
      <>
        {cloneElement(trigger, { onClick: () => setOpen(true) })}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="gap-0 rounded-t-2xl bg-card p-2 pb-6">
            <SheetTitle className="sr-only">{label}</SheetTitle>
            <SheetDescription className="sr-only">{label}</SheetDescription>
            {actions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() => {
                  setOpen(false)
                  action.onSelect()
                }}
                className={cn(
                  'flex min-h-12 w-full items-start gap-2.5 rounded-lg px-4 py-2.5 text-left hover:bg-white/[0.06]',
                  action.destructive && 'text-destructive'
                )}
              >
                <ActionText action={action} />
              </button>
            ))}
          </SheetContent>
        </Sheet>
      </>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={className}
        onCloseAutoFocus={(event) => {
          const action = chosen.current
          chosen.current = null
          if (!action) return
          event.preventDefault()
          action.onSelect()
        }}
      >
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.key}
            className={cn(action.hint && 'items-start gap-2.5 rounded-md px-3 py-2')}
            variant={action.destructive ? 'destructive' : 'default'}
            onSelect={() => {
              chosen.current = action
            }}
          >
            <ActionText action={action} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
